import { HeroUIProvider, ToastProvider } from '@heroui/react';
import * as Sentry from '@sentry/react';
import { type FC, useEffect } from 'react';

import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Outlet,
  RouterProvider,
  useHref,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { PostHogIdentity } from '@/app/analytics/PostHogIdentity.tsx';
import { PostHogPageView } from '@/app/analytics/PostHogPageView.tsx';
import { isPostHogConfigured } from '@/app/analytics/posthog.ts';
import Layout from '@/app/components/layout.tsx';
import { PageLoading } from '@/app/components/page-loading.tsx';
import { AuthProvider, useAuth } from '@/app/hooks/auth-provider.tsx';

import childRoutes from '../../child-routes.ts';
import { SecurityProvider } from '../../hooks/security-provider.tsx';
import LoginPage from '../auth/login.tsx';
import ResetPasswordPage from '../auth/reset-password.tsx';
import ErrorBoundary from '../error-boundary/ErrorBoundary.lazy.tsx';
import { representativeSignUpRoute } from '../onboard/RepresentativeSignUp.lazy.tsx';
import { qrConnectRoute } from '../qr-connect/QrConnect.lazy.tsx';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  environment: import.meta.env.MODE,
  // Disable Sentry if no DSN is provided
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  // Tracing
  tracesSampleRate: import.meta.env.MODE === 'development' ? 0 : 1.0, // Capture 0% in dev, 100% in prod
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/meetingmd-app-prod-h2c2hmacg8hzhsdy\.eastus2-01\.azurewebsites\.net\/api/,
  ],
  // Session Replay
  //replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const RootRoute: FC = () => {
  const navigate = useNavigate();

  return (
    <AuthProvider>
      {isPostHogConfigured ? (
        <>
          <PostHogIdentity />
          <PostHogPageView />
        </>
      ) : null}
      <SecurityProvider>
        <HeroUIProvider navigate={navigate} useHref={useHref}>
          <Outlet />
          <ToastProvider placement='top-center' />
        </HeroUIProvider>
      </SecurityProvider>
    </AuthProvider>
  );
};

const RequireAuth: FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  const redirectTo = `${location.pathname}${location.search ?? ''}`;

  if (isLoading) {
    return <PageLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ redirectTo }} replace />;
  }

  return <Layout />;
};

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <RootRoute />,
    children: [
      qrConnectRoute,
      representativeSignUpRoute,
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: '/',
        element: <RequireAuth />,
        children: childRoutes,
        errorElement: <ErrorBoundary />,
      },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} fallbackElement={<PageLoading />} />;
}
